import { useEffect, useRef } from 'react';

/** 한 프레임에 밀어내는 거리(px). 60fps 기준 초당 약 36px 로 기존 마퀴 속도와 비슷하다. */
const SPEED = 0.6;

/** 손을 뗀 뒤 자동 흐름을 다시 시작하기까지 기다리는 시간(ms) */
const RESUME_DELAY = 1800;

/**
 * 가로로 끝없이 흐르는 목록을 스크롤 컨테이너로 다루는 훅.
 * 목록을 두 번 이어 붙였다는 전제로, 절반을 지나면 같은 위치로 되돌려 이음매를 감춘다.
 * 마우스로 끌어 과거 카드로 되돌아갈 수 있고, 터치는 브라우저 기본 스크롤에 맡긴다.
 */
export default function useDragScroll(speed = SPEED) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let hovering = false;
    let dragging = false;
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let resumeTimer = 0;
    let holding = false; // 터치로 넘기는 중
    let raf = 0;

    /** 이어 붙인 목록의 한 벌 길이 */
    const half = () => el.scrollWidth / 2;

    const wrap = () => {
      const h = half();
      if (h <= 0) return;
      if (el.scrollLeft >= h) el.scrollLeft -= h;
      else if (el.scrollLeft < 0) el.scrollLeft += h;
    };

    const step = () => {
      if (!reduce && !hovering && !dragging && !holding) {
        el.scrollLeft += speed;
        wrap();
      }
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);

    const onEnter = () => {
      hovering = true;
    };
    const onLeave = () => {
      hovering = false;
    };

    const onPointerDown = (e) => {
      window.clearTimeout(resumeTimer);
      if (e.pointerType === 'touch') {
        holding = true;
        return;
      }
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      el.dataset.dragging = 'true';
      if (el.setPointerCapture) el.setPointerCapture(pointerId);
    };

    const onPointerMove = (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      el.scrollLeft = startLeft - (e.clientX - startX);
      wrap();
      e.preventDefault();
    };

    const endDrag = (e) => {
      if (e && e.pointerType === 'touch') {
        holding = false;
        // 관성 스크롤이 끝난 뒤에 다시 흐르게 한다.
        resumeTimer = window.setTimeout(wrap, RESUME_DELAY);
        return;
      }
      if (!dragging) return;
      dragging = false;
      delete el.dataset.dragging;
      if (pointerId !== null && el.releasePointerCapture) {
        try {
          el.releasePointerCapture(pointerId);
        } catch (err) {
          /* 이미 해제된 포인터 */
        }
      }
      pointerId = null;
    };

    // 드래그한 직후의 클릭은 삼킨다 (카드 안 링크 오동작 방지)
    const onClick = (e) => {
      if (el.dataset.dragging) e.preventDefault();
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('click', onClick);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(resumeTimer);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('click', onClick);
    };
  }, [speed]);

  return ref;
}
